import { create, fromBinary, toBinary } from '@bufbuild/protobuf';

import {
  LoginRequestSchema as UnauthorizedLoginRequestSchema,
  RefreshTokenRequestSchema as UnauthorizedRefreshTokenRequestSchema,
} from '@gem/call/WsApi/unauthorizedWsCallsApi/types/auth/auth_requests_pb';
import {
  LoginResponseSchema as UnauthorizedLoginResponseSchema,
  RefreshTokenResponseSchema as UnauthorizedRefreshTokenResponseSchema,
} from '@gem/call/WsApi/unauthorizedWsCallsApi/types/auth/auth_respones_pb';
import { logger } from '@gem/modules/logger';

import {
  mapLoginResponse,
  mapRefreshTokenResponse,
  mapUnauthorizedRefreshTokenResponse,
  mapUnauthorizedLoginResponse,
  mapRefreshTokenRequest,
  mapLoginByCodeRequest,
  mapGetAuthUrlResponse,
  mapGetRegistrationUrlResponse,
} from 'APP/model/auth/authModel.mapper';
import {
  IUnauthorizedLogin,
  ILoginSuccess,
  ILogout,
  IRefreshToken,
  IUnauthorizedLoginSuccess,
  IUnauthorizedRefreshToken,
  ILoginByCode,
  IAuthUrlSuccess,
  IRegistrationUrlSuccess,
  LoginErrors,
} from 'APP/model/auth/authModel.types';
import { buildModelError } from 'APP/model/error/errorBuilder';
import api from 'APP/restApi';
import { LoginDeviceInfoSchema } from 'APP/wsApi/wsAppApi/types/auth/auth_model_pb';
import {
  LoginByCodeRequestSchema,
  LogoutRequestSchema,
  RefreshTokenRequestSchema,
} from 'APP/wsApi/wsAppApi/types/auth/auth_requests_pb';
import {
  GetAuthUrlResponseSchema,
  GetRegistrationUrlResponseSchema,
  LoginResponseSchema,
  LogoutResponseSchema,
  RefreshTokenResponseSchema,
} from 'APP/wsApi/wsAppApi/types/auth/auth_respones_pb';

export const loginByCode = async (data: ILoginByCode): Promise<ILoginSuccess> => {
  try {
    const mappedData = mapLoginByCodeRequest(data);
    const requestData = create(LoginByCodeRequestSchema, {
      ...mappedData,
      device: create(LoginDeviceInfoSchema, mappedData.device),
    });

    const result = await api.authorization.loginByCode(
      toBinary(LoginByCodeRequestSchema, requestData)
    );
    const response = fromBinary(LoginResponseSchema, new Uint8Array(result));

    if (response.type.case === 'deviceIsBlocked') {
      throw new Error(LoginErrors.DeviceIsBlocked);
    }

    if (response.type.case !== 'success') {
      throw new Error('Error while login by code');
    }

    return mapLoginResponse(response.type.value);
  } catch (error) {
    logger.get('API').error('authorization.loginByCode', error);
    throw buildModelError(error);
  }
};

export const unauthorizedLogin = async (
  data: IUnauthorizedLogin
): Promise<IUnauthorizedLoginSuccess> => {
  try {
    const payload = create(UnauthorizedLoginRequestSchema, data);

    const result = await api.authorization.unauthorizedLogin(
      toBinary(UnauthorizedLoginRequestSchema, payload)
    );

    const response = fromBinary(UnauthorizedLoginResponseSchema, new Uint8Array(result));

    if (response.type.case !== 'success') {
      throw new Error('Error while unauthorized login');
    }

    return mapUnauthorizedLoginResponse(response.type.value);
  } catch (error) {
    logger.get('API').error('authorization.unauthorizedLogin', error);
    throw buildModelError(error);
  }
};

export const refreshToken = async (data: IRefreshToken): Promise<ILoginSuccess> => {
  try {
    const mappedData = mapRefreshTokenRequest(data);
    const requestData = create(RefreshTokenRequestSchema, {
      ...mappedData,
      device: create(LoginDeviceInfoSchema, mappedData.device),
    });

    const result = await api.authorization.refreshToken(
      toBinary(RefreshTokenRequestSchema, requestData)
    );

    const response = fromBinary(RefreshTokenResponseSchema, new Uint8Array(result));

    if (response.type.case !== 'success') {
      throw new Error('Error while refreshing token');
    }

    return mapRefreshTokenResponse(response.type.value);
  } catch (error) {
    logger.get('API').error('authorization.refreshToken', error);
    throw buildModelError(error);
  }
};

export const unauthorizedRefreshToken = async (
  data: IUnauthorizedRefreshToken
): Promise<IUnauthorizedLoginSuccess> => {
  try {
    const payload = create(UnauthorizedRefreshTokenRequestSchema, data);

    const result = await api.authorization.unauthorizedRefreshToken(
      toBinary(UnauthorizedRefreshTokenRequestSchema, payload)
    );

    const response = fromBinary(UnauthorizedRefreshTokenResponseSchema, new Uint8Array(result));

    if (response.type.case !== 'success') {
      throw new Error('Error while unauthorized refreshing token');
    }

    return mapUnauthorizedRefreshTokenResponse(response.type.value);
  } catch (error) {
    logger.get('API').error('authorization.refreshToken', error);
    throw buildModelError(error);
  }
};

export const logout = async (data: ILogout): Promise<boolean> => {
  try {
    const payload = create(LogoutRequestSchema, data);

    const result = await api.authorization.logout(toBinary(LogoutRequestSchema, payload));

    const response = fromBinary(LogoutResponseSchema, new Uint8Array(result));

    if (response.type.case !== 'success') {
      throw new Error('Error while logging out');
    }

    return true;
  } catch (error) {
    logger.get('API').error('authorization.logout', error);
    throw buildModelError(error);
  }
};

export async function getAuthUrl(): Promise<IAuthUrlSuccess> {
  try {
    const result = await api.authorization.getAuthUrl();
    const response = fromBinary(GetAuthUrlResponseSchema, new Uint8Array(result));

    if (response.type.case !== 'success') {
      throw new Error('Error while getting url to active directory authorization page');
    }

    return mapGetAuthUrlResponse(response.type.value);
  } catch (error) {
    logger.get('API').error('authorization.getAuthUrl', error);
    throw buildModelError(error);
  }
}

export async function getRegistrationUrl(): Promise<IRegistrationUrlSuccess> {
  try {
    const result = await api.authorization.getRegistrationUrl();
    const response = fromBinary(GetRegistrationUrlResponseSchema, new Uint8Array(result));

    if (response.type.case !== 'success') {
      throw new Error('Error while getting url to active directory registration page');
    }

    return mapGetRegistrationUrlResponse(response.type.value);
  } catch (error) {
    logger.get('API').error('authorization.getAuthUrl', error);
    throw buildModelError(error);
  }
}
